import dayjs, { Dayjs } from 'dayjs'
import { createDefaultWorksheet } from '../../../budgetCalculatorModel'
import { adminEmployeeAnnualCost, calculateHoursWorkedPerYear, OVERTIME_THRESHOLD_HOURS_PER_WEEK } from '../../employeeCostModel'
import { uniqueId } from '../../../../../helpers/uniqueId'
import type { OverheadGroup } from '../../../overheadModel'
import type { Material } from '../../../materialsModel'
import type {
  Worksheet,
  CompanyFormData,
  SeasonFormData,
  OwnerFormData,
  FieldEmployeesFormData,
  AdminEmployeesFormData,
  TrucksFormData,
  TrailersFormData,
  HeavyMachineryFormData
} from '../types'

export const calculateWorkingWeeks = (startDate: Dayjs, endDate: Dayjs): number => {
  const weeks = endDate.diff(startDate, 'week')
  return Math.max(1, weeks)
}

export {
  calculateHoursWorkedPerYear,
  OVERTIME_THRESHOLD_HOURS_PER_WEEK
} from '../../employeeCostModel'

type RevenueDistribution = {
  designBuild: { percentage: number; revenue: number }
  maintenance: { percentage: number; revenue: number }
  snow: { percentage: number; revenue: number }
}

const calculateRevenueDistribution = (
  revenueSource: string,
  seasonalSnowWork: boolean,
  salesGoal: number
): RevenueDistribution => {
  let designBuildPct = 0
  let maintenancePct = 0

  switch (revenueSource) {
    case 'only_install':
      designBuildPct = 100
      maintenancePct = 0
      break
    case 'mostly_install':
      designBuildPct = 75
      maintenancePct = 25
      break
    case 'half_install_half_maintenance':
      designBuildPct = 50
      maintenancePct = 50
      break
    case 'mostly_maintenance':
      designBuildPct = 25
      maintenancePct = 75
      break
    case 'only_maintenance':
      designBuildPct = 0
      maintenancePct = 100
      break
    default:
      designBuildPct = 50
      maintenancePct = 50
  }

  let snowPct = 0
  if (seasonalSnowWork) {
    if (designBuildPct > 0 && maintenancePct > 0) {
      designBuildPct -= 5
      maintenancePct -= 5
      snowPct = 10
    } else if (designBuildPct > 0) {
      designBuildPct -= 10
      snowPct = 10
    } else if (maintenancePct > 0) {
      maintenancePct -= 10
      snowPct = 10
    }
  }

  return {
    designBuild: {
      percentage: designBuildPct,
      revenue: salesGoal * (designBuildPct / 100)
    },
    maintenance: {
      percentage: maintenancePct,
      revenue: salesGoal * (maintenancePct / 100)
    },
    snow: {
      percentage: snowPct,
      revenue: salesGoal * (snowPct / 100)
    }
  }
}

const updateOverheadFromSalesGoal = (
  overheadGroups: OverheadGroup[],
  salesGoal: number
): OverheadGroup[] => {
  const calculations = [
    { group: 'Advertising & Marketing', description: 'Website', multiplier: 0.003 },
    { group: 'Advertising & Marketing', description: 'Advertising', multiplier: 0.015 },
    { group: 'Advertising & Marketing', description: 'Uniforms', multiplier: 0.0023 },
    { group: 'Advertising & Marketing', description: 'Client gifts', multiplier: 0.0008 },
    { group: 'Donations & Charity', description: 'Local sponsorships', multiplier: 0.0002 },
    { group: 'Donations & Charity', description: 'Donations', multiplier: 0.0005 },
    { group: 'Donations & Charity', description: 'Charity', multiplier: 0.0005 },
    { group: 'Overhead Staff', description: 'Marketing staff', multiplier: 0.001 },
    { group: 'Overhead Staff', description: 'Payroll service', multiplier: 0.0025 },
    { group: 'Overhead Staff', description: 'Payroll taxes', multiplier: 0.025 },
    { group: 'Overhead Staff', description: 'Gifts/bonuses', multiplier: 0.0009 },
    { group: 'Bank & Financial Charges', description: 'Bank fees', multiplier: 0.0002 },
    { group: 'Bank & Financial Charges', description: 'Credit card processing', multiplier: 0.005 },
    { group: 'Bank & Financial Charges', description: 'Interest expense', multiplier: 0.002 },
    { group: 'Bank & Financial Charges', description: 'Taxes & licenses', multiplier: 0.0025 },
    { group: 'Equipment & Truck', description: 'Equipment maintenance', multiplier: 0.0079 },
    { group: 'Equipment & Truck', description: 'Truck maintenance', multiplier: 0.0095 },
    { group: 'Equipment & Truck', description: 'Fuel', multiplier: 0.025 },
    { group: 'Equipment & Truck', description: 'Rentals', multiplier: 0.001 },
    { group: 'Professional Services', description: 'Consulting', multiplier: 0.0035 },
    { group: 'Professional Services', description: 'Accountant', multiplier: 0.005 },
    { group: 'Professional Services', description: 'Insurance', multiplier: 0.0185 },
    { group: 'Professional Services', description: 'Professional fees', multiplier: 0.0035 },
    { group: 'Small Equipment', description: 'Job trailer tools', multiplier: 0.0045 },
    { group: 'Small Equipment', description: 'Small engine equipment', multiplier: 0.0007 },
    { group: 'Subscriptions & Dues', description: 'Subscriptions', multiplier: 0.008 },
    { group: 'Subscriptions & Dues', description: 'Postage & delivery', multiplier: 0.002 },
    { group: 'Travel', description: 'Travel', multiplier: 0.003 },
    { group: 'Travel', description: 'Meals', multiplier: 0.0011 },
    { group: 'Travel', description: 'Meetings/training', multiplier: 0.003 },
    { group: 'Travel', description: 'Trade shows/events', multiplier: 0.0011 },
    { group: 'Utilities', description: 'Internet', multiplier: 0.0012 },
    { group: 'Utilities', description: 'Cell phones', multiplier: 0.0015 },
    { group: 'Utilities', description: 'Water', multiplier: 0.0001 },
    { group: 'Utilities', description: 'Electricity', multiplier: 0.0007 },
    { group: 'Miscellaneous', description: 'Garbage disposal', multiplier: 0.0006 },
    { group: 'Miscellaneous', description: 'Office supplies', multiplier: 0.002 },
    { group: 'Miscellaneous', description: 'Shop rent', multiplier: 0.0126 },
    { group: 'Miscellaneous', description: 'Other', multiplier: 0.003 }
  ]

  return overheadGroups.map(group => {
    const groupCalculations = calculations.filter(c => c.group === group.groupName)
    if (groupCalculations.length === 0) return group

    return {
      ...group,
      overhead: group.overhead.map(item => {
        const calc = groupCalculations.find(c => c.description === item.description)
        if (!calc) return item

        return {
          ...item,
          annualCost: Math.round(salesGoal * calc.multiplier)
        }
      })
    }
  })
}

const addAdminEmployeesToOverhead = (
  overheadGroups: OverheadGroup[],
  adminEmployeesData: AdminEmployeesFormData,
  workingWeeks: number
): OverheadGroup[] => {
  return overheadGroups.map(group => {
    if (group.groupName !== 'Overhead Staff') return group

    const adminEmployeeOverheadItems = adminEmployeesData.employees.map(emp => ({
      id: uniqueId(),
      description: emp.name,
      annualCost: Math.round(adminEmployeeAnnualCost(emp, workingWeeks))
    }))

    return {
      ...group,
      overhead: [...group.overhead, ...adminEmployeeOverheadItems]
    }
  })
}

export const buildWorksheet = (
  companyData: CompanyFormData,
  seasonData: SeasonFormData,
  ownerData: OwnerFormData,
  fieldEmployeesData: FieldEmployeesFormData,
  adminEmployeesData: AdminEmployeesFormData,
  trucksData: TrucksFormData,
  trailersData: TrailersFormData,
  heavyMachineryData: HeavyMachineryFormData,
  budgetBuilderVersion: 'legacy' | 'experimental'
): Worksheet => {
  const worksheet = createDefaultWorksheet()
  worksheet.title = dayjs().format('MMMM D, YYYY') + ' Budget'

  const workingWeeks = calculateWorkingWeeks(
    seasonData.seasonStartDate!,
    seasonData.seasonEndDate!
  )

  const hourlyFieldEmployees = fieldEmployeesData.employees
    .filter(emp => emp.payType === 'Hourly')
    .map(emp => {
      const hoursPerWeek = emp.hoursPerWeek || 0
      const hoursWorkedPerYear = calculateHoursWorkedPerYear(hoursPerWeek, workingWeeks)
      const overtimeHoursPerWeek = Math.max(0, hoursPerWeek - OVERTIME_THRESHOLD_HOURS_PER_WEEK)

      return {
        id: uniqueId(),
        name: emp.name,
        role: '',
        hourlyWage: emp.hourlyRate || 0,
        hoursWorkedPerYear,
        unbillableHoursPerYear: Math.round(hoursWorkedPerYear * 0.15),
        overtimeHours: overtimeHoursPerWeek * workingWeeks,
        bonuses: 0,
        overtimeMultiplier: 1.5
      }
    })

  const salaryFieldEmployees = fieldEmployeesData.employees
    .filter(emp => emp.payType === 'Salary')
    .map(emp => {
      const hoursWorkedPerYear = workingWeeks * 40

      return {
        id: uniqueId(),
        name: emp.name,
        role: '',
        annualSalary: emp.annualSalary || 0,
        hoursWorkedPerYear,
        unbillableHoursPerYear: Math.round(hoursWorkedPerYear * 0.15),
        overtimeHours: 0,
        bonuses: 0
      }
    })


  const ownerHoursPerYear = (ownerData.hoursPerWeek || 0) * 52
  const ownerFieldPercentage = (ownerData.fieldWorkPercentage || 0) / 100
  const ownerUnbillableHours = Math.round(ownerHoursPerYear * (1 - ownerFieldPercentage))

  const ownerEmployee = {
    id: uniqueId(),
    name: 'Owner',
    role: '',
    annualSalary: ownerData.annualSalary || 0,
    hoursWorkedPerYear: ownerHoursPerYear,
    unbillableHoursPerYear: ownerUnbillableHours,
    overtimeHours: 0,
    bonuses: 0
  }

  worksheet.data.employees = {
    hourly: [...hourlyFieldEmployees],
    salary: [...salaryFieldEmployees, ownerEmployee]
  }
  const ownedTrucks = trucksData.trucks
    .filter(truck => truck.ownershipType === 'Own')
    .map(truck => ({
      id: uniqueId(),
      name: truck.name,
      quantity: 1,
      recoveryStrategy: 'OVERHEAD' as const,
      billableHoursPerYear: 0,
      purchasePrice: truck.replacementCost || 0,
      yearsUsed: truck.avgUsefulLife || 1,
      sellPrice: truck.sellPrice || 0
    }))
  const leasedTrucks = trucksData.trucks
    .filter(truck => truck.ownershipType === 'Lease/Finance')
    .map(truck => ({
      id: uniqueId(),
      name: truck.name,
      quantity: 1,
      recoveryStrategy: 'OVERHEAD' as const,
      billableHoursPerYear: 0,
      monthlyPayment: truck.monthlyPayment || 0
    }))
  const ownedTrailers = trailersData.trailers
    .filter(trailer => trailer.ownershipType === 'Own')
    .map(trailer => ({
      id: uniqueId(),
      name: trailer.name,
      quantity: 1,
      recoveryStrategy: 'OVERHEAD' as const,
      billableHoursPerYear: 0,
      purchasePrice: trailer.replacementCost || 0,
      yearsUsed: trailer.avgUsefulLife || 1,
      sellPrice: trailer.sellPrice || 0
    }))
  const leasedTrailers = trailersData.trailers
    .filter(trailer => trailer.ownershipType === 'Lease/Finance')
    .map(trailer => ({
      id: uniqueId(),
      name: trailer.name,
      quantity: 1,
      recoveryStrategy: 'OVERHEAD' as const,
      billableHoursPerYear: 0,
      monthlyPayment: trailer.monthlyPayment || 0
    }))
  const ownedHeavyMachinery = heavyMachineryData.machinery
    .filter(machinery => machinery.ownershipType === 'Own')
    .map(machinery => ({
      id: uniqueId(),
      name: machinery.equipmentName === 'Other'
        ? (machinery.customEquipmentName || 'Other')
        : machinery.equipmentName,
      quantity: 1,
      recoveryStrategy: 'OVERHEAD' as const,
      billableHoursPerYear: 0,
      purchasePrice: machinery.replacementCost || 0,
      yearsUsed: machinery.avgUsefulLife || 1,
      sellPrice: machinery.sellPrice || 0
    }))
  const leasedHeavyMachinery = heavyMachineryData.machinery
    .filter(machinery => machinery.ownershipType === 'Lease/Finance')
    .map(machinery => ({
      id: uniqueId(),
      name: machinery.equipmentName === 'Other'
        ? (machinery.customEquipmentName || 'Other')
        : machinery.equipmentName,
      quantity: 1,
      recoveryStrategy: 'OVERHEAD' as const,
      billableHoursPerYear: 0,
      monthlyPayment: machinery.monthlyPayment || 0
    }))
  const allOwnedEquipment = [...ownedTrucks, ...ownedTrailers, ...ownedHeavyMachinery]
  const allLeasedEquipment = [...leasedTrucks, ...leasedTrailers, ...leasedHeavyMachinery]
  worksheet.data.equipmentList = {
    owned: allOwnedEquipment.length > 0 ? allOwnedEquipment : worksheet.data.equipmentList.owned,
    leased: allLeasedEquipment.length > 0 ? allLeasedEquipment : worksheet.data.equipmentList.leased,
    misc: worksheet.data.equipmentList.misc
  }

  const revenueDistribution = calculateRevenueDistribution(
    companyData.revenueSource || 'half_install_half_maintenance',
    companyData.seasonalSnowWork,
    companyData.salesGoal || 0
  )

  const materials: Material[] = []

  if (revenueDistribution.designBuild.revenue > 0) {
    materials.push({
      id: uniqueId(),
      description: 'Install materials',
      annualCost: Math.round(revenueDistribution.designBuild.revenue * 0.35)
    })
  }

  if (revenueDistribution.maintenance.revenue > 0) {
    materials.push({
      id: uniqueId(),
      description: 'Maintenance materials',
      annualCost: Math.round(revenueDistribution.maintenance.revenue * 0.15)
    })
  }

  if (revenueDistribution.snow.revenue > 0) {
    materials.push({
      id: uniqueId(),
      description: 'Snow materials',
      annualCost: Math.round(revenueDistribution.snow.revenue * 0.15)
    })
  }

  if (materials.length === 0) {
    materials.push({
      id: uniqueId(),
      description: '',
      annualCost: 0
    })
  }

  worksheet.data.materials = materials

  worksheet.data.profit.season.startDate = seasonData.seasonStartDate!.format('YYYY-MM-DD')
  worksheet.data.profit.season.endDate = seasonData.seasonEndDate!.format('YYYY-MM-DD')
  worksheet.data.profit.season.workingDaysPerWeek = seasonData.daysPerWeek || 5
  worksheet.data.profit.desiredNetProfitMargin = (companyData.profitMargin || 0) / 100
  worksheet.data.profit.desiredSalesGoal = companyData.salesGoal || 0

  const salesGoal = companyData.salesGoal || 0
  worksheet.data.overheadGroups = updateOverheadFromSalesGoal(
    worksheet.data.overheadGroups,
    salesGoal
  )

  worksheet.data.overheadGroups = addAdminEmployeesToOverhead(
    worksheet.data.overheadGroups,
    adminEmployeesData,
    workingWeeks
  )

  const subcontracted = companyData.subcontractedAmount || 0
  if (subcontracted > 0) {
    worksheet.data.subcontractors = [
      { id: uniqueId(), description: 'Subcontracted work', annualCost: Math.round(subcontracted) }
    ]
  }

  worksheet.metadata = {
    createdVia: 'budget-builder',
    budgetBuilderVersion
  }

  return worksheet
}
